<app-header />

@if (isLoading) {
  <!-- Loading State -->
  <div class="max-w-7xl mx-auto px-6 py-20 text-center">
    <p class="text-gray-600">Cargando evento...</p>
  </div>
} @else if (error || !event) {
  <!-- Error State -->
  <div class="max-w-7xl mx-auto px-6 py-20 text-center">
    <h2 class="text-2xl font-bold text-gray-800 mb-4">Evento no encontrado</h2>
    <p class="text-gray-600 mb-6">El evento que buscas no existe o no está disponible.</p>
    <button 
      (click)="goBack()"
      class="bg-[#e8343f] text-white px-6 py-3 rounded-lg hover:bg-red-700 transition-colors"
    >
      Volver al inicio
    </button>
  </div>
} @else {
  <!-- Event Image Banner -->
  <section class="border-b border-gray-300">
    <div class="max-w-7xl mx-auto px-6 pt-2 pb-4">
      <div class="aspect-[40/21] bg-gray-100 border border-gray-300 overflow-hidden rounded-2xl">
        <img 
          [src]="getEventImage('horizontal')" 
          [alt]="event.nombre"
          class="w-full h-full object-cover"
        />
      </div>
    </div>
  </section>

  <!-- Event Information Section -->
  <section class="py-8 border-b border-gray-300">
    <div class="max-w-7xl mx-auto px-6">
      @if (isTrenEvent()) {
        <!-- Layout para evento tipo tren - Todo el ancho -->
        <div class="space-y-8">
          <!-- Información básica del evento sin fecha_hora -->
          <div class="space-y-6">
            <!-- Title and Category -->
            <div>
              <span class="inline-block mb-2 text-sm bg-gray-200 text-gray-700 px-4 py-1 rounded-full">
                {{ event.categoria }}
              </span>
              <h1 class="text-4xl font-bold text-gray-900 mb-4">{{ event.nombre }}</h1>
            </div>

            <!-- Location -->
            <div class="flex items-start space-x-3">
              <svg class="w-6 h-6 text-gray-600 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/>
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"/>
              </svg>
              <div>
                <p class="text-lg font-semibold text-gray-800">Ubicación</p>
                <p class="text-gray-600">{{ event.lugar }}</p>
              </div>
            </div>

            <!-- Description -->
            <div class="pt-4 border-t border-gray-200">
              <h3 class="text-xl font-bold text-gray-900 mb-3">Descripción</h3>
              @if (event.contenido_html) {
                <div class="text-gray-700 leading-relaxed prose prose-sm max-w-none" [innerHTML]="sanitizeHtml(event.contenido_html)"></div>
              } @else {
                <p class="text-gray-700 leading-relaxed whitespace-pre-line">{{ event.descripcion }}</p>
              }
            </div>
          </div>

          <!-- Sección de Actividades (Subeventos) -->
          <div class="pt-4">
            <h2 class="text-2xl font-bold text-gray-900 mb-6">Actividades</h2>

            @if (isLoadingSubeventos) {
              <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                @for (i of [1,2,3]; track i) {
                  <div class="animate-pulse bg-gray-100 rounded-2xl h-80"></div>
                }
              </div>
            } @else if (errorSubeventos) {
              <div class="bg-red-50 border border-red-200 rounded-2xl p-8 text-center">
                <svg class="w-10 h-10 mx-auto mb-3 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                </svg>
                <p class="text-red-700 font-medium">No se pudieron cargar las actividades.</p>
                <p class="text-red-500 text-sm mt-1">Por favor intenta recargar la página.</p>
                <button
                  (click)="loadSubeventos(event.eventoid)"
                  class="mt-4 px-5 py-2 bg-[#e8343f] text-white rounded-lg text-sm font-medium hover:bg-red-700 transition-colors"
                >
                  Reintentar
                </button>
              </div>
            } @else if (subeventos.length === 0) {
              <div class="text-center py-12">
                <p class="text-gray-500">No hay actividades disponibles para este evento.</p>
              </div>
            } @else {
              <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                @for (subevento of subeventos; track subevento.subeventoid) {
                  <div
                    (click)="navigateToActividad(subevento)"
                    class="bg-white rounded-2xl border border-gray-200 overflow-hidden cursor-pointer hover:shadow-lg hover:border-[#e8343f] transition-all duration-200 group flex flex-col"
                  >
                    <!-- Imagen cuadrada -->
                    <div class="aspect-square overflow-hidden">
                      <img
                        [src]="getSubeventoImage(subevento, 'cuadrada')"
                        [alt]="subevento.nombre"
                        class="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                    <!-- Nombre y contenido -->
                    <div class="p-5 flex-1">
                      <h3 class="text-xl font-bold text-gray-900 mb-3">{{ subevento.nombre }}</h3>
                      @if (subevento.contenido_html) {
                        <div
                          class="text-gray-600 text-sm leading-relaxed prose prose-sm max-w-none"
                          [innerHTML]="sanitizeHtml(subevento.contenido_html)"
                        ></div>
                      } @else if (subevento.descripcion) {
                        <p class="text-gray-600 text-sm leading-relaxed">{{ subevento.descripcion }}</p>
                      }
                    </div>
                    <!-- Footer con botón Comprar -->
                    <div class="px-5 pb-5 pt-3">
                      <button class="w-full bg-[#e8343f] hover:bg-[#c42a33] text-white font-semibold text-sm py-2.5 rounded-xl transition-colors duration-200 text-center">
                        Comprar
                      </button>
                    </div>
                  </div>
                }
              </div>
            }
          </div>

          <!-- Mapa después de actividades -->
          <div class="w-full">
            @if (event.localizacion && event.localizacion.lat && event.localizacion.lng) {
              <div 
                #mapContainer 
                class="w-full h-[400px] bg-gray-100 border border-gray-300 overflow-hidden rounded-2xl"
              ></div>
            } @else {
              <!-- Map Placeholder -->
              <div class="w-full h-[400px] bg-gray-100 border border-gray-300 overflow-hidden rounded-2xl flex items-center justify-center">
                <div class="text-center text-gray-500">
                  <svg class="w-12 h-12 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7"/>
                  </svg>
                  <p class="text-sm">Ubicación no disponible</p>
                </div>
              </div>
            }
          </div>
        </div>
      } @else {
        <!-- Layout normal para eventos default - 2 columnas -->
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <!-- Event Details -->
          <div class="space-y-6">
            <!-- Title and Category -->
            <div>
              <span class="inline-block mb-2 text-sm bg-gray-200 text-gray-700 px-4 py-1 rounded-full">
                {{ event.categoria }}
              </span>
              <h1 class="text-4xl font-bold text-gray-900 mb-4">{{ event.nombre }}</h1>
            </div>

            <!-- Date and Time -->
            <div class="flex items-start space-x-3">
              <svg class="w-6 h-6 text-gray-600 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/>
              </svg>
              <div>
                <p class="text-lg font-semibold text-gray-800">{{ formatDate(event.fecha_hora) }}</p>
                <p class="text-gray-600">{{ formatTime(event.fecha_hora) }}</p>
              </div>
            </div>

            <!-- Location -->
            <div class="flex items-start space-x-3">
              <svg class="w-6 h-6 text-gray-600 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/>
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"/>
              </svg>
              <div>
                <p class="text-lg font-semibold text-gray-800">Ubicación</p>
                <p class="text-gray-600">{{ event.lugar }}</p>
              </div>
            </div>

            <!-- Description -->
            <div class="pt-4 border-t border-gray-200">
              <h3 class="text-xl font-bold text-gray-900 mb-3">Descripción</h3>
              <p class="text-gray-700 leading-relaxed whitespace-pre-line">{{ event.descripcion }}</p>
            </div>

            <!-- Additional Info -->
            <div class="pt-4 border-t border-gray-200 space-y-2">
              @if (event.configuracion.mostrar_aforo_disponible) {
                <div class="flex items-center text-sm text-gray-600">
                  <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"/>
                  </svg>
                  Se muestra aforo disponible
                </div>
              }
              @if (event.configuracion.permitir_venta) {
                <div class="flex items-center text-sm text-green-600">
                  <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                  </svg>
                  Venta disponible
                </div>
              }
              <div class="flex items-center text-sm text-gray-600">
                <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
                </svg>
                Tiempo de reserva: {{ event.tiempo_expiracion_reserva }} minutos
              </div>
            </div>

            <!-- Ticket Selector - Solo para eventos que no son tren -->
            @if (event.configuracion.permitir_venta && !isTrenEvent()) {
              <div class="pt-6">
                @if (isLoadingDefaultTipoTickets) {
                  <div class="bg-gray-50 rounded-2xl p-6 border border-gray-300 text-center">
                    <p class="text-gray-600">Cargando localidades...</p>
                  </div>
                } @else if (errorDefaultTipoTickets) {
                  <div class="bg-red-50 rounded-2xl p-6 border border-red-200 text-center">
                    <p class="text-red-700">No se pudieron cargar las localidades.</p>
                  </div>
                } @else if (hasSeatmapInDefaultEvent) {
                  <div class="bg-gray-50 rounded-2xl p-6 border border-gray-300">
                    <div class="mb-4">
                      <div class="flex items-center mb-2">
                        <span class="bg-white rounded-full w-8 h-8 flex items-center justify-center border-2 border-gray-800 text-sm font-bold mr-3">1</span>
                        <h2 class="text-lg font-semibold text-gray-800">Selecciona tu localidad</h2>
                      </div>
                      <p class="text-sm text-gray-600">Este evento usa mapa de asientos. Elige la localidad para continuar.</p>
                    </div>

                    <div class="bg-white rounded-xl overflow-hidden border border-gray-200">
                      @for (tipoTicket of defaultTipoTickets; track tipoTicket.tipoticketid) {
                        <label class="flex items-start gap-3 p-4 border-b border-gray-100 last:border-b-0 cursor-pointer hover:bg-gray-50">
                          <input
                            type="radio"
                            name="default-tipoticket"
                            [value]="tipoTicket.tipoticketid"
                            [(ngModel)]="selectedDefaultTipoTicketId"
                            class="mt-1"
                          />
                          <div class="flex-1">
                            <p class="font-semibold text-gray-900">{{ tipoTicket.nombre_localidad }}</p>
                            <p class="text-sm text-gray-600">
                              Disponibles: {{ tipoTicket.aforo_disponible }} / {{ tipoTicket.aforo_maximo }}
                            </p>
                            @if (getDefaultTipoTicketPrecioDesde(tipoTicket) !== null) {
                              <p class="text-sm text-gray-700 mt-1">
                                Desde <strong>${{ getDefaultTipoTicketPrecioDesde(tipoTicket)!.toFixed(2) }} {{ getDefaultTipoTicketMoneda(tipoTicket) }}</strong>
                              </p>
                            }
                          </div>
                        </label>
                      }
                    </div>

                    <button
                      (click)="goToComprarLocalidad()"
                      [disabled]="!selectedDefaultTipoTicketId"
                      class="mt-4 w-full bg-[#e8343f] text-white py-3 rounded-lg text-base font-semibold hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Continuar
                    </button>
                  </div>
                } @else {
                  <app-ticket-selector
                    [eventoid]="event.eventoid"
                    [url_slug]="event.url_slug"
                    [event]="event"
                    (ticketsAdded)="onTicketsAdded()"
                  />
                }
              </div>
            }
          </div>
          
          <!-- Event Image and Map -->
          <div class="space-y-6">
            <!-- Event Image -->
            <div class="aspect-square bg-gray-100 border border-gray-300 overflow-hidden rounded-2xl">
              <img 
                [src]="getEventImage('cuadrada')" 
                [alt]="event.nombre"
                class="w-full h-full object-cover"
              />
            </div>

            <!-- Map -->
            @if (event.localizacion && event.localizacion.lat && event.localizacion.lng) {
              <div 
                #mapContainer 
                class="aspect-video bg-gray-100 border border-gray-300 overflow-hidden rounded-2xl"
              ></div>
            } @else {
              <!-- Map Placeholder -->
              <div class="aspect-video bg-gray-100 border border-gray-300 overflow-hidden rounded-2xl flex items-center justify-center">
                <div class="text-center text-gray-500">
                  <svg class="w-12 h-12 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7"/>
                  </svg>
                  <p class="text-sm">Ubicación no disponible</p>
                </div>
              </div>
            }
          </div>
        </div>
      }
    </div>
  </section>
}

<app-footer />
<app-scroll-to-top />
